import { Button, Group, Stack, Table, Text, Title } from "@mantine/core";
import { IconUsers } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  FIT_REASON_FULL_COUNT,
  FIT_REASON_PREVIEW_COUNT,
  LOG_CONTACT_LABEL,
  MATCH_TABLE_ACTION,
  MATCH_TABLE_CANDIDATE,
  MATCH_TABLE_CONTACT,
  MATCH_TABLE_FIT,
  MATCH_TABLE_WHY,
  POOL_MATCHES_DESCRIPTION,
  POOL_MATCHES_EMPTY_DESCRIPTION,
  POOL_MATCHES_EMPTY_TITLE,
  POOL_MATCHES_TITLE,
} from "../../constants/matching";
import { jobsApi } from "../../lib/api";
import { ContentCard, DataTableShell, useVisibleWindow } from "../ui";
import { ContactActions } from "./ContactActions";
import { ContactStatus } from "./ContactStatus";
import { FitReasonList } from "./FitReasonList";
import { FitTierBadge } from "./FitTierBadge";
import { LogContactModal } from "./LogContactModal";
import { SUBMIT_TO_JOB_LABEL } from "../SubmitCandidateModal";
import type { PoolCandidateMatch } from "../../types/api";

export function JobPoolMatchesSection({
  jobId,
  onSubmit,
}: {
  jobId: string;
  onSubmit: (candidateId: string, candidateName: string) => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [logCandidateId, setLogCandidateId] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ["job-pool-matches", jobId],
    queryFn: () => jobsApi.poolMatches(jobId),
    enabled: !!jobId,
  });
  const matches = query.data ?? [];
  const matchWindow = useVisibleWindow(matches, jobId);

  return (
    <ContentCard>
      <Stack gap="md">
        <Stack gap="var(--axio-unit-6)">
          <Title order={5}>{POOL_MATCHES_TITLE}</Title>
          <Text size="sm" c="dimmed">
            {POOL_MATCHES_DESCRIPTION}
          </Text>
        </Stack>
        <DataTableShell
          withBorder={false}
          loading={query.isLoading}
          error={query.isError ? (query.error instanceof Error ? query.error.message : undefined) : undefined}
          onRetry={() => void query.refetch()}
          hasMore={matchWindow.hasMore}
          loadingMore={matchWindow.loadingMore}
          onLoadMore={matchWindow.loadMore}
          isEmpty={matches.length === 0}
          empty={{
            icon: IconUsers,
            title: POOL_MATCHES_EMPTY_TITLE,
            description: POOL_MATCHES_EMPTY_DESCRIPTION,
          }}
        >
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{MATCH_TABLE_CANDIDATE}</Table.Th>
                <Table.Th>{MATCH_TABLE_FIT}</Table.Th>
                <Table.Th>{MATCH_TABLE_WHY}</Table.Th>
                <Table.Th>{MATCH_TABLE_CONTACT}</Table.Th>
                <Table.Th>{MATCH_TABLE_ACTION}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {matchWindow.items.map((match) => (
                <PoolRow
                  key={match.match_id}
                  match={match}
                  jobId={jobId}
                  expanded={expanded === match.match_id}
                  onToggle={() => setExpanded(expanded === match.match_id ? null : match.match_id)}
                  onSubmit={() => onSubmit(match.candidate_id, match.candidate_name)}
                  onLog={() => setLogCandidateId(match.candidate_id)}
                />
              ))}
            </Table.Tbody>
          </Table>
        </DataTableShell>
      </Stack>
      {logCandidateId ? (
        <LogContactModal
          opened
          onClose={() => setLogCandidateId(null)}
          candidateId={logCandidateId}
          jobId={jobId}
        />
      ) : null}
    </ContentCard>
  );
}

function PoolRow({
  match,
  jobId,
  expanded,
  onToggle,
  onSubmit,
  onLog,
}: {
  match: PoolCandidateMatch;
  jobId: string;
  expanded: boolean;
  onToggle: () => void;
  onSubmit: () => void;
  onLog: () => void;
}) {
  return (
    <>
      <Table.Tr onClick={onToggle} style={{ cursor: "pointer" }}>
        <Table.Td>
          <Stack gap="var(--axio-unit-2)">
            <Text
              component={Link}
              to={`/candidates/${match.candidate_id}`}
              fw="var(--axio-font-weight-semibold)"
              size="sm"
            >
              {match.candidate_name}
            </Text>
            <Text size="xs" c="dimmed">
              {match.current_title ?? match.phone}
            </Text>
          </Stack>
        </Table.Td>
        <Table.Td>
          <FitTierBadge tier={match.fit_tier} score={match.fit_score} />
        </Table.Td>
        <Table.Td>
          <FitReasonList reasons={match.fit_reasons} limit={FIT_REASON_PREVIEW_COUNT} />
        </Table.Td>
        <Table.Td>
          <ContactStatus lastContactedAt={match.last_contacted_at} lastContactOutcome={match.last_contact_outcome} />
        </Table.Td>
        <Table.Td>
          <Group gap="xs" wrap="nowrap" onClick={(event) => event.stopPropagation()}>
            <ContactActions phone={match.phone} candidateId={match.candidate_id} jobId={jobId} />
            <Button size="xs" variant="default" onClick={onLog}>
              {LOG_CONTACT_LABEL}
            </Button>
            <Button size="xs" variant="default" onClick={onSubmit}>
              {SUBMIT_TO_JOB_LABEL}
            </Button>
          </Group>
        </Table.Td>
      </Table.Tr>
      {expanded ? (
        <Table.Tr>
          <Table.Td colSpan={5}>
            <FitReasonList reasons={match.fit_reasons} limit={FIT_REASON_FULL_COUNT} />
          </Table.Td>
        </Table.Tr>
      ) : null}
    </>
  );
}
